import axios from 'axios'

const apiClient = axios.create({
  baseURL: (import.meta as any).env.VITE_API_URL || '',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null
  return null
}

let refreshPromise: Promise<boolean> | null = null
let refreshTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Schedule a proactive silent refresh before the access token expires.
 */
export function scheduleSilentRefresh(expiresAtIsoString?: string) {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  if (!expiresAtIsoString) {
    return
  }

  const expiresTime = new Date(expiresAtIsoString).getTime()
  const now = Date.now()
  const timeUntilExpiry = expiresTime - now

  if (isNaN(timeUntilExpiry) || timeUntilExpiry <= 0) {
    return
  }

  // Refresh 2 minutes before expiry, or at 75% of remaining lifespan if shorter
  const refreshAdvanceMs = Math.min(120000, Math.max(10000, timeUntilExpiry * 0.25))
  const delay = Math.max(5000, timeUntilExpiry - refreshAdvanceMs)

  refreshTimer = setTimeout(async () => {
    const success = await refreshAuthSession()
    if (!success) {
      // Retry once after 30s before the access token dies completely
      setTimeout(() => {
        refreshAuthSession()
      }, 30000)
    }
  }, delay)
}

/**
 * Cancel any scheduled silent refresh timer (e.g. on logout).
 */
export function clearSilentRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

/**
 * Execute or join an in-flight token refresh request.
 * Deduplicates multiple concurrent refresh calls into a single HTTP request to avoid
 * race conditions with Keycloak refresh token rotation.
 */
export async function refreshAuthSession(): Promise<boolean> {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    try {
      const baseURL = (import.meta as any).env.VITE_API_URL || ''
      const currentCsrf =
        apiClient.defaults.headers.common['X-CSRF-Token'] || getCookie('csrf_token') || ''

      const response = await axios.post(
        `${baseURL}/api/auth/refresh`,
        {},
        {
          withCredentials: true,
          headers: {
            'X-CSRF-Token': currentCsrf
          }
        }
      )

      // El BFF emite un nuevo csrf_token como cookie no HttpOnly
      const newCsrf = getCookie('csrf_token')
      if (newCsrf) {
        apiClient.defaults.headers.common['X-CSRF-Token'] = newCsrf
      }

      if (response.data?.expires_at) {
        scheduleSilentRefresh(response.data.expires_at)
      }

      return true
    } catch (err) {
      return false
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/api/auth/login') &&
      !originalRequest.url?.includes('/api/auth/refresh')
    ) {
      originalRequest._retry = true

      const success = await refreshAuthSession()
      if (success) {
        const newCsrf = getCookie('csrf_token') || apiClient.defaults.headers.common['X-CSRF-Token']
        if (newCsrf && originalRequest.headers) {
          originalRequest.headers['X-CSRF-Token'] = newCsrf
        }
        return apiClient(originalRequest)
      } else {
        clearSilentRefresh()
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient
